import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input"]
  static values = {
    uploadUrl: String,
    deleteUrl: String,
    max: Number,
    min: Number,
    directUpload: { type: Boolean, default: false }
  }

  connect() {
    this.#updateInputState()
  }

  upload() {
    const files = Array.from(this.inputTarget.files)
    if (files.length === 0) return

    const currentCount = this.#currentCount()
    if (currentCount + files.length > this.maxValue) {
      this.#showError(`Adding ${files.length} image(s) would exceed the ${this.maxValue}-image limit. Please remove some images first or select fewer.`)
      this.inputTarget.value = ""
      return
    }

    files.forEach(file => this.#uploadFile(file))
    this.inputTarget.value = ""
  }

  delete({ params: { signedId } }) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.deleteUrlValue, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html",
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: `signed_id=${encodeURIComponent(signedId)}`
    }).then(response => {
      if (!response.ok) {
        this.#showError("Delete failed. Please try again.")
        return
      }
      return response.text().then(html => {
        Turbo.renderStreamMessage(html)
        requestAnimationFrame(() => this.#updateInputState())
      })
    }).catch(() => {
      this.#showError("Delete failed. Please try again.")
    })
  }

  // Private

  #uploadFile(file) {
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      this.#showError(`${file.name} is not a supported format. Use JPEG or PNG.`)
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      this.#showError(`${file.name} exceeds the 10MB size limit.`)
      return
    }

    const placeholder = this.#insertPlaceholder()

    if (this.directUploadValue) {
      this.#directUploadFile(file, placeholder)
    } else {
      this.#formUploadFile(file, placeholder)
    }
  }

  #directUploadFile(file, placeholder) {
    const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads")
    upload.create((error, blob) => {
      if (error) {
        if (placeholder) placeholder.remove()
        this.#showError(`Upload failed for ${file.name}. Please try again.`)
        return
      }

      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: `signed_id=${encodeURIComponent(blob.signed_id)}`
      }).then(response => {
        if (placeholder) placeholder.remove()
        return response.text().then(html => {
          Turbo.renderStreamMessage(html)
          requestAnimationFrame(() => this.#updateInputState())
        })
      }).catch(() => {
        if (placeholder) placeholder.remove()
        this.#showError(`Upload failed for ${file.name}. Please try again.`)
      })
    })
  }

  #formUploadFile(file, placeholder) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const formData = new FormData()
    formData.append("file", file)

    fetch(this.uploadUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: formData
    }).then(response => {
      if (placeholder) placeholder.remove()
      if (!response.ok) {
        return response.text().then(html => {
          Turbo.renderStreamMessage(html)
        })
      }
      return response.text().then(html => {
        Turbo.renderStreamMessage(html)
        requestAnimationFrame(() => this.#updateInputState())
      })
    }).catch(() => {
      if (placeholder) placeholder.remove()
      this.#showError(`Upload failed for ${file.name}. Please try again.`)
    })
  }

  #insertPlaceholder() {
    const grid = document.getElementById("gallery-images-grid")
    if (!grid) return null
    const div = document.createElement("div")
    div.className = "d-inline-flex align-items-center justify-content-center border rounded"
    div.style.cssText = "width: 120px; height: 120px;"
    div.setAttribute("role", "status")
    div.innerHTML = '<div class="spinner-border spinner-border-sm text-muted" role="status"><span class="visually-hidden">Uploading...</span></div>'
    grid.appendChild(div)
    return div
  }

  #updateInputState() {
    const atMax = this.#currentCount() >= this.maxValue
    this.inputTarget.disabled = atMax
    if (atMax) {
      this.inputTarget.classList.add("d-none")
    } else {
      this.inputTarget.classList.remove("d-none")
    }
    const hint = document.getElementById("gallery-images-max-hint")
    if (hint) {
      hint.classList.toggle("d-none", !atMax)
    }
  }

  #showError(message) {
    const errorDiv = document.getElementById("gallery-images-error")
    if (!errorDiv) return
    const alert = document.createElement("div")
    alert.className = "alert alert-danger border-0 py-1 mt-1 mb-0"
    alert.textContent = message
    errorDiv.replaceChildren(alert)
    setTimeout(() => { errorDiv.replaceChildren() }, 5000)
  }

  #currentCount() {
    const grid = document.getElementById("gallery-images-grid")
    if (!grid) return 0
    return grid.querySelectorAll("[id^='gallery-image-']").length
  }
}
